<template>
    <div>
        <message-standard-rule
            v-if="FEATURES.locked_snippets && !props.hidden?.includes('locked_snippets')"
            :modelValue="rules.locked_snippets"
            @update:modelValue="lockedSnippetsToggled"
            :disabled="props.disabled?.locked_snippets"
        >
            <template v-slot:title>Locked Snippets Only</template>
            <template v-slot:description>
                Users can only select locked snippets in message builder. They won’t be able to edit the message.
            </template>
        </message-standard-rule>

        <message-standard-rule
            v-if="!rules.locked_snippets && !props.hidden?.includes('block_images')"
            v-model="rules.block_images"
            :disabled="props.disabled?.block_images"
        >
            <template v-slot:title>Block Images</template>
        </message-standard-rule>

        <message-standard-rule
            v-if="!rules.locked_snippets && !props.hidden?.includes('block_files')"
            v-model="rules.block_files"
            :disabled="props.disabled?.block_files"
        >
            <template v-slot:title>Block Files</template>
        </message-standard-rule>

        <message-standard-rule
            v-if="!rules.locked_snippets && !props.hidden?.includes('maximum_emoji')"
            :modelValue="rules.maximum_emoji !== null"
            @update:modelValue="(enabled) => (rules.maximum_emoji = enabled ? 3 : null)"
            :disabled="props.disabled?.maximum_emoji"
        >
            <template v-slot:title>Limit Emoji Usage</template>

            <div>
                <vx-label> Maximum Emoji Count </vx-label>
                <vx-input v-model="rules.maximum_emoji" type="number" size="md" />
            </div>
        </message-standard-rule>

        <message-standard-rule
            v-if="!rules.locked_snippets && !props.hidden?.includes('maximum_sms_segments')"
            :modelValue="rules.maximum_sms_segments !== null"
            @update:modelValue="(enabled) => (rules.maximum_sms_segments = enabled ? 10 : null)"
            :disabled="props.disabled?.maximum_sms_segments"
        >
            <template v-slot:title>Limit Segment Count</template>

            <div>
                <vx-label>
                    <div>Maximum Segment Count <span class="font-normal">(estimated)</span></div>
                </vx-label>
                <vx-input v-model="rules.maximum_sms_segments" type="number" size="md" />
            </div>
        </message-standard-rule>

        <message-standard-rule
            v-if="!rules.locked_snippets && !props.hidden?.includes('maximum_characters')"
            :modelValue="rules.maximum_characters !== null"
            @update:modelValue="(enabled) => (rules.maximum_characters = enabled ? 1600 : null)"
            :disabled="props.disabled?.maximum_characters"
        >
            <template v-slot:title>Limit Character Count</template>

            <div>
                <vx-label>
                    <div>Maximum Character Count <span class="font-normal">(estimated)</span></div>
                </vx-label>
                <vx-input v-model="rules.maximum_characters" type="number" size="md" />
            </div>
        </message-standard-rule>

        <message-standard-rule
            v-if="!rules.locked_snippets && !props.hidden?.includes('blocked_words')"
            :modelValue="rules.blocked_words !== null"
            @update:modelValue="
                (enabled) => {
                    if (!enabled) rules.blocked_words = null;
                    else rules.blocked_words = [];
                }
            "
            :disabled="props.disabled?.blocked_words"
        >
            <template v-slot:title>Block Words</template>
            <template v-slot:description>
                Prevent the usage of certain words or phrases. Words are
                <br class="hidden lg:block" />case-<strong>insensitive</strong>.
            </template>

            <div>
                <div class="flex gap-2 mb-2 flex-col lg:flex-row">
                    <div class="grow">
                        <vx-label> Word or Phrase </vx-label>
                        <vx-input v-model="wordValue" size="lg" />
                    </div>
                    <vx-button @click="addBlockedWord()" data-test="tag-add" size="lg" class="lg:mt-6" type="button">
                        <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                    </vx-button>
                </div>
            </div>

            <vx-alert v-if="!rules.blocked_words?.length" :closeable="false"> No blocked words added yet. </vx-alert>

            <vx-list-animated v-if="rules.blocked_words !== null" v-model="rules.blocked_words" class="mt-2" />
        </message-standard-rule>

        <message-standard-rule
            v-if="!props.hidden?.includes('send_frequency_cadence')"
            :modelValue="rules.send_frequency_cadence !== null && rules.send_frequency_limit !== null"
            @update:modelValue="
                (enabled) => {
                    if (!enabled) {
                        rules.send_frequency_cadence = null;
                        rules.send_frequency_limit = null;
                    } else {
                        rules.send_frequency_cadence = 'week';
                        rules.send_frequency_limit = 1;
                    }
                }
            "
            :disabled="props.disabled?.send_frequency_cadence"
        >
            <template v-slot:title>Limit Send Frequency</template>
            <template v-slot:description> Limit how many quick blasts can be sent over a period of time. </template>

            <div
                v-if="rules.send_frequency_limit !== null && rules.send_frequency_cadence !== null"
                class="flex items-center gap-2"
            >
                <div class="flex-1">
                    <vx-label>Limit</vx-label>
                    <vx-input v-model="rules.send_frequency_limit" type="number" size="md" min="0" max="100" />
                </div>
                <span class="text-sm text-slate-700 mt-6">per</span>
                <div class="flex-1">
                    <vx-label>Time Period</vx-label>
                    <!-- @vue-ignore -->
                    <vx-selectable
                        v-model="rules.send_frequency_cadence"
                        :clearable="false"
                        :reduce="(option) => option.value"
                        :options="periodOptions"
                        size="md"
                    />
                </div>
            </div>
        </message-standard-rule>

        <message-standard-rule
            v-if="!props.hidden?.includes('min_scheduling_days')"
            :modelValue="rules.min_scheduling_days !== null"
            @update:modelValue="(enabled) => (rules.min_scheduling_days = enabled ? 1 : null)"
            :disabled="props.disabled?.min_scheduling_days"
        >
            <template v-slot:title>Scheduling Delay</template>
            <template v-slot:description> Require groups to schedule blasts at least 1–14 days in advance. </template>

            <div v-if="rules.min_scheduling_days !== null">
                <vx-label>Minimum Days in Advance</vx-label>
                <!-- @vue-ignore -->
                <vx-selectable
                    v-model="rules.min_scheduling_days"
                    :clearable="false"
                    :reduce="(option) => option.value"
                    :options="schedulingDelayOptions"
                    size="md"
                />
            </div>
        </message-standard-rule>

        <message-standard-rule
            v-if="!props.hidden?.includes('max_scheduling_weeks')"
            :modelValue="rules.max_scheduling_weeks !== null"
            @update:modelValue="(enabled) => (rules.max_scheduling_weeks = enabled ? 1 : null)"
            :disabled="props.disabled?.max_scheduling_weeks"
        >
            <template v-slot:title>Scheduling Limit</template>
            <template v-slot:description> Restrict groups from scheduling blasts more than 1–12 weeks ahead. </template>

            <div v-if="rules.max_scheduling_weeks !== null">
                <vx-label
                    ><div>Limit <span class="font-normal">(weeks)</span></div></vx-label
                >
                <!-- @vue-ignore -->
                <vx-selectable
                    v-model="rules.max_scheduling_weeks"
                    :clearable="false"
                    :reduce="(option) => option.value"
                    :options="scheduledLimitOptions"
                    size="md"
                />
            </div>
        </message-standard-rule>

        <message-standard-rule
            v-if="!props.hidden?.includes('permitted_sending_windows')"
            :modelValue="rules.permitted_sending_windows !== null"
            @update:modelValue="
                (enabled) => {
                    if (!enabled) rules.permitted_sending_windows = null;
                    else rules.permitted_sending_windows = defaultPermittedSendingWindows();
                }
            "
            :disabled="props.disabled?.permitted_sending_windows"
        >
            <template v-slot:title>Safe Sending Days and Hours</template>
            <template v-slot:description>Only allow sending on specific days and times.</template>
            <message-standard-safe-sending-times
                v-if="rules.permitted_sending_windows !== null"
                v-model="rules.permitted_sending_windows"
                class="mt-4"
            />
        </message-standard-rule>
    </div>
</template>

<script setup lang="ts">
import { VxInput, VxSelectable, VxLabel, VxListAnimated, VxButton, VxAlert } from '@voxie/frontend-components';
import MessageStandardSafeSendingTimes from './MessageStandardSafeSendingTimes.vue';
import type { MessageStandardRulesType } from '~/services/messageStandards/messageStandardsClient.types';
import MessageStandardRule from './MessageStandardRule.vue';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { ref, inject } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { defaultPermittedSendingWindows } from './useMessageStandardDetails';
import type { Features } from '~/provides/features.types';

const FEATURES = inject('FEATURES') as Features;

const props = defineProps<{
    disabled?: { [key in keyof MessageStandardRulesType]?: string };
    hidden?: (keyof MessageStandardRulesType)[];
}>();

const rules = defineModel<MessageStandardRulesType>({ default: () => ({}) });

const wordValue = ref('');
const addBlockedWord = () => {
    if (!wordValue.value) {
        return;
    }
    if (rules.value.blocked_words === null) {
        rules.value.blocked_words = [];
    }
    rules.value.blocked_words.push(wordValue.value);
    wordValue.value = '';
};

const periodOptions = [
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
];

const schedulingDelayOptions = [
    { label: '1 - no same day scheduling', value: 1 },
    { label: '2 - no same or next day scheduling', value: 2 },
    { label: '3 - no scheduling within 3 days', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 },
    { label: '6', value: 6 },
    { label: '7', value: 7 },
    { label: '8', value: 8 },
    { label: '9', value: 9 },
    { label: '10', value: 10 },
    { label: '11', value: 11 },
    { label: '12', value: 12 },
    { label: '13', value: 13 },
    { label: '14', value: 14 },
];

const scheduledLimitOptions = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 },
    { label: '6', value: 6 },
    { label: '7', value: 7 },
    { label: '8', value: 8 },
    { label: '9', value: 9 },
    { label: '10', value: 10 },
    { label: '11', value: 11 },
    { label: '12', value: 12 },
];
const lockedSnippetsToggled = (lockedSnippets: boolean) => {
    rules.value.locked_snippets = lockedSnippets;

    if (lockedSnippets) {
        rules.value.block_images = false;
        rules.value.block_files = false;
        rules.value.maximum_emoji = null;
        rules.value.maximum_sms_segments = null;
        rules.value.maximum_characters = null;
        rules.value.blocked_words = null;
    }
};
</script>
